/**
 * WhatsApp Flows helper — constants, component definitions, and JSON generation.
 *
 * Meta Flows JSON spec v6.0:
 * Each flow has screens, each screen has a SingleColumnLayout with children.
 * Children are form components (TextInput, Dropdown, etc.) and display components (TextHeading, etc.).
 * The last child should be a Footer component with on-click-action (navigate/complete).
 */

export const FLOW_VERSION = '6.0';

// ── Component types available in the palette ─────────────────────────
export const FLOW_COMPONENT_TYPES = {
  // Display components
  TEXT_HEADING: 'TextHeading',
  TEXT_SUBHEADING: 'TextSubheading',
  TEXT_BODY: 'TextBody',
  TEXT_CAPTION: 'TextCaption',
  IMAGE: 'Image',
  EMBEDDED_LINK: 'EmbeddedLink',

  // Input components
  TEXT_INPUT: 'TextInput',
  TEXT_AREA: 'TextArea',
  DROPDOWN: 'Dropdown',
  RADIO_BUTTONS_GROUP: 'RadioButtonsGroup',
  CHECKBOX_GROUP: 'CheckboxGroup',
  DATE_PICKER: 'DatePicker',
  OPT_IN: 'OptIn',

  // Action
  FOOTER: 'Footer',
};

// ── Palette category groupings ───────────────────────────────────────
export const COMPONENT_CATEGORIES = [
  {
    key: 'display',
    label: 'WHATSAPP_FLOWS.PALETTE.DISPLAY',
    components: [
      FLOW_COMPONENT_TYPES.TEXT_HEADING,
      FLOW_COMPONENT_TYPES.TEXT_SUBHEADING,
      FLOW_COMPONENT_TYPES.TEXT_BODY,
      FLOW_COMPONENT_TYPES.TEXT_CAPTION,
      FLOW_COMPONENT_TYPES.IMAGE,
      FLOW_COMPONENT_TYPES.EMBEDDED_LINK,
    ],
  },
  {
    key: 'input',
    label: 'WHATSAPP_FLOWS.PALETTE.INPUT',
    components: [
      FLOW_COMPONENT_TYPES.TEXT_INPUT,
      FLOW_COMPONENT_TYPES.TEXT_AREA,
      FLOW_COMPONENT_TYPES.DROPDOWN,
      FLOW_COMPONENT_TYPES.RADIO_BUTTONS_GROUP,
      FLOW_COMPONENT_TYPES.CHECKBOX_GROUP,
      FLOW_COMPONENT_TYPES.DATE_PICKER,
      FLOW_COMPONENT_TYPES.OPT_IN,
    ],
  },
  {
    key: 'action',
    label: 'WHATSAPP_FLOWS.PALETTE.ACTION',
    components: [FLOW_COMPONENT_TYPES.FOOTER],
  },
];

// ── Icons for each component type ────────────────────────────────────
export const COMPONENT_ICONS = {
  [FLOW_COMPONENT_TYPES.TEXT_HEADING]: 'i-lucide-heading-1',
  [FLOW_COMPONENT_TYPES.TEXT_SUBHEADING]: 'i-lucide-heading-2',
  [FLOW_COMPONENT_TYPES.TEXT_BODY]: 'i-lucide-type',
  [FLOW_COMPONENT_TYPES.TEXT_CAPTION]: 'i-lucide-text',
  [FLOW_COMPONENT_TYPES.IMAGE]: 'i-lucide-image',
  [FLOW_COMPONENT_TYPES.EMBEDDED_LINK]: 'i-lucide-link',
  [FLOW_COMPONENT_TYPES.TEXT_INPUT]: 'i-lucide-text-cursor-input',
  [FLOW_COMPONENT_TYPES.TEXT_AREA]: 'i-lucide-align-left',
  [FLOW_COMPONENT_TYPES.DROPDOWN]: 'i-lucide-chevrons-up-down',
  [FLOW_COMPONENT_TYPES.RADIO_BUTTONS_GROUP]: 'i-lucide-circle-dot',
  [FLOW_COMPONENT_TYPES.CHECKBOX_GROUP]: 'i-lucide-check-square',
  [FLOW_COMPONENT_TYPES.DATE_PICKER]: 'i-lucide-calendar',
  [FLOW_COMPONENT_TYPES.OPT_IN]: 'i-lucide-toggle-left',
  [FLOW_COMPONENT_TYPES.FOOTER]: 'i-lucide-mouse-pointer-click',
};

// ── User-friendly labels ─────────────────────────────────────────────
export const COMPONENT_LABELS = {
  [FLOW_COMPONENT_TYPES.TEXT_HEADING]: 'Heading',
  [FLOW_COMPONENT_TYPES.TEXT_SUBHEADING]: 'Subheading',
  [FLOW_COMPONENT_TYPES.TEXT_BODY]: 'Body Text',
  [FLOW_COMPONENT_TYPES.TEXT_CAPTION]: 'Caption',
  [FLOW_COMPONENT_TYPES.IMAGE]: 'Image',
  [FLOW_COMPONENT_TYPES.EMBEDDED_LINK]: 'Link',
  [FLOW_COMPONENT_TYPES.TEXT_INPUT]: 'Text Input',
  [FLOW_COMPONENT_TYPES.TEXT_AREA]: 'Text Area',
  [FLOW_COMPONENT_TYPES.DROPDOWN]: 'Dropdown',
  [FLOW_COMPONENT_TYPES.RADIO_BUTTONS_GROUP]: 'Radio Buttons',
  [FLOW_COMPONENT_TYPES.CHECKBOX_GROUP]: 'Checkbox Group',
  [FLOW_COMPONENT_TYPES.DATE_PICKER]: 'Date Picker',
  [FLOW_COMPONENT_TYPES.OPT_IN]: 'Opt-In',
  [FLOW_COMPONENT_TYPES.FOOTER]: 'Submit Button',
};

// ── Component descriptions for the palette ───────────────────────────
export const COMPONENT_DESCRIPTIONS = {
  [FLOW_COMPONENT_TYPES.TEXT_HEADING]: 'WHATSAPP_FLOWS.PALETTE.DESC_HEADING',
  [FLOW_COMPONENT_TYPES.TEXT_SUBHEADING]:
    'WHATSAPP_FLOWS.PALETTE.DESC_SUBHEADING',
  [FLOW_COMPONENT_TYPES.TEXT_BODY]: 'WHATSAPP_FLOWS.PALETTE.DESC_BODY',
  [FLOW_COMPONENT_TYPES.TEXT_CAPTION]: 'WHATSAPP_FLOWS.PALETTE.DESC_CAPTION',
  [FLOW_COMPONENT_TYPES.IMAGE]: 'WHATSAPP_FLOWS.PALETTE.DESC_IMAGE',
  [FLOW_COMPONENT_TYPES.EMBEDDED_LINK]: 'WHATSAPP_FLOWS.PALETTE.DESC_LINK',
  [FLOW_COMPONENT_TYPES.TEXT_INPUT]: 'WHATSAPP_FLOWS.PALETTE.DESC_TEXT_INPUT',
  [FLOW_COMPONENT_TYPES.TEXT_AREA]: 'WHATSAPP_FLOWS.PALETTE.DESC_TEXT_AREA',
  [FLOW_COMPONENT_TYPES.DROPDOWN]: 'WHATSAPP_FLOWS.PALETTE.DESC_DROPDOWN',
  [FLOW_COMPONENT_TYPES.RADIO_BUTTONS_GROUP]:
    'WHATSAPP_FLOWS.PALETTE.DESC_RADIO',
  [FLOW_COMPONENT_TYPES.CHECKBOX_GROUP]: 'WHATSAPP_FLOWS.PALETTE.DESC_CHECKBOX',
  [FLOW_COMPONENT_TYPES.DATE_PICKER]: 'WHATSAPP_FLOWS.PALETTE.DESC_DATE_PICKER',
  [FLOW_COMPONENT_TYPES.OPT_IN]: 'WHATSAPP_FLOWS.PALETTE.DESC_OPT_IN',
  [FLOW_COMPONENT_TYPES.FOOTER]: 'WHATSAPP_FLOWS.PALETTE.DESC_FOOTER',
};

// ── Flow actions ─────────────────────────────────────────────────────
export const FLOW_ACTIONS = {
  NAVIGATE: 'navigate',
  COMPLETE: 'complete',
  DATA_EXCHANGE: 'data_exchange',
};

// ── Default property values for new components ───────────────────────
export function createDefaultComponent(type) {
  const id = `${type}_${Date.now()}`;
  const base = { type, _id: id };

  switch (type) {
    case FLOW_COMPONENT_TYPES.TEXT_HEADING:
      return { ...base, text: 'Heading' };
    case FLOW_COMPONENT_TYPES.TEXT_SUBHEADING:
      return { ...base, text: 'Subheading' };
    case FLOW_COMPONENT_TYPES.TEXT_BODY:
      return { ...base, text: 'Body text goes here' };
    case FLOW_COMPONENT_TYPES.TEXT_CAPTION:
      return { ...base, text: 'Caption text' };
    case FLOW_COMPONENT_TYPES.IMAGE:
      return { ...base, src: '', height: 200, 'scale-type': 'contain' };
    case FLOW_COMPONENT_TYPES.EMBEDDED_LINK:
      return {
        ...base,
        text: 'Learn more',
        'on-click-action': {
          name: 'navigate',
          next: { type: 'screen', name: '' },
          payload: {},
        },
      };
    case FLOW_COMPONENT_TYPES.TEXT_INPUT:
      return {
        ...base,
        label: 'Text Input',
        name: `text_input_${Date.now()}`,
        required: false,
        'input-type': 'text',
        'helper-text': '',
      };
    case FLOW_COMPONENT_TYPES.TEXT_AREA:
      return {
        ...base,
        label: 'Text Area',
        name: `text_area_${Date.now()}`,
        required: false,
        'helper-text': '',
      };
    case FLOW_COMPONENT_TYPES.DROPDOWN:
      return {
        ...base,
        label: 'Select an option',
        name: `dropdown_${Date.now()}`,
        required: false,
        'data-source': [
          { id: '1', title: 'Option 1' },
          { id: '2', title: 'Option 2' },
        ],
      };
    case FLOW_COMPONENT_TYPES.RADIO_BUTTONS_GROUP:
      return {
        ...base,
        label: 'Choose one',
        name: `radio_${Date.now()}`,
        required: false,
        'data-source': [
          { id: '1', title: 'Option 1' },
          { id: '2', title: 'Option 2' },
        ],
      };
    case FLOW_COMPONENT_TYPES.CHECKBOX_GROUP:
      return {
        ...base,
        label: 'Select all that apply',
        name: `checkbox_${Date.now()}`,
        required: false,
        'data-source': [
          { id: '1', title: 'Option 1' },
          { id: '2', title: 'Option 2' },
        ],
      };
    case FLOW_COMPONENT_TYPES.DATE_PICKER:
      return {
        ...base,
        label: 'Select a date',
        name: `date_${Date.now()}`,
        required: false,
        'helper-text': '',
      };
    case FLOW_COMPONENT_TYPES.OPT_IN:
      return {
        ...base,
        label: 'I agree to the terms',
        name: `opt_in_${Date.now()}`,
        required: false,
      };
    case FLOW_COMPONENT_TYPES.FOOTER:
      return {
        ...base,
        label: 'Continue',
        'on-click-action': {
          name: FLOW_ACTIONS.COMPLETE,
          payload: {},
        },
      };
    default:
      return base;
  }
}

// ── Screen helpers ───────────────────────────────────────────────────
let screenCounter = 0;

export function createDefaultScreen(isTerminal = false) {
  screenCounter += 1;
  const id = `SCREEN_${screenCounter}`;
  return {
    id,
    title: `Screen ${screenCounter}`,
    terminal: isTerminal,
    layout: {
      type: 'SingleColumnLayout',
      children: [
        createDefaultComponent(FLOW_COMPONENT_TYPES.TEXT_HEADING),
        createDefaultComponent(FLOW_COMPONENT_TYPES.FOOTER),
      ],
    },
  };
}

export function resetScreenCounter() {
  screenCounter = 0;
}

// ── Generate clean Flow JSON (strip internal _id fields) ─────────────
function cleanComponent(component) {
  // eslint-disable-next-line no-underscore-dangle
  const { _id, ...rest } = component;
  return rest;
}

function cleanScreen(screen) {
  return {
    id: screen.id,
    title: screen.title,
    terminal: screen.terminal || false,
    layout: {
      type: 'SingleColumnLayout',
      children: screen.layout.children.map(child => cleanComponent(child)),
    },
  };
}

export function buildFlowJSON(screens) {
  return {
    version: FLOW_VERSION,
    screens: screens.map(screen => cleanScreen(screen)),
  };
}

// ── Check if a component is an input type (collects data) ────────────
export function isInputComponent(type) {
  return [
    FLOW_COMPONENT_TYPES.TEXT_INPUT,
    FLOW_COMPONENT_TYPES.TEXT_AREA,
    FLOW_COMPONENT_TYPES.DROPDOWN,
    FLOW_COMPONENT_TYPES.RADIO_BUTTONS_GROUP,
    FLOW_COMPONENT_TYPES.CHECKBOX_GROUP,
    FLOW_COMPONENT_TYPES.DATE_PICKER,
    FLOW_COMPONENT_TYPES.OPT_IN,
  ].includes(type);
}

// ── Extract all variable names from screens ──────────────────────────
export function extractFlowVariables(screens) {
  const variables = [];
  screens.forEach(screen => {
    (screen.layout?.children || []).forEach(child => {
      if (isInputComponent(child.type) && child.name) {
        variables.push({
          screen: screen.id,
          name: child.name,
          type: child.type,
          label: child.label,
        });
      }
    });
  });
  return variables;
}

// ── Validate flow JSON against WhatsApp requirements ──────────────
export function validateFlowScreens(screens) {
  const errors = [];

  if (!screens.length) {
    errors.push({
      screen: null,
      message: 'WHATSAPP_FLOWS.VALIDATION.NO_SCREENS',
    });
    return errors;
  }

  const screenIds = new Set();
  const allVariableNames = new Set();

  screens.forEach((screen, sIdx) => {
    const prefix = `Screen ${sIdx + 1} ("${screen.title}")`;

    // Screen ID validations
    if (!screen.id || !screen.id.trim()) {
      errors.push({
        screen: sIdx,
        message: 'WHATSAPP_FLOWS.VALIDATION.MISSING_SCREEN_ID',
        args: { screen: prefix },
      });
    } else if (screenIds.has(screen.id)) {
      errors.push({
        screen: sIdx,
        message: 'WHATSAPP_FLOWS.VALIDATION.DUPLICATE_SCREEN_ID',
        args: { screen: prefix, id: screen.id },
      });
    }
    screenIds.add(screen.id);

    if (!screen.title || !screen.title.trim()) {
      errors.push({
        screen: sIdx,
        message: 'WHATSAPP_FLOWS.VALIDATION.MISSING_SCREEN_TITLE',
        args: { screen: prefix },
      });
    }

    const children = screen.layout?.children || [];

    if (!children.length) {
      errors.push({
        screen: sIdx,
        message: 'WHATSAPP_FLOWS.VALIDATION.EMPTY_SCREEN',
        args: { screen: prefix },
      });
      return;
    }

    // Must have exactly one Footer
    const footers = children.filter(c => c.type === 'Footer');
    if (footers.length === 0) {
      errors.push({
        screen: sIdx,
        message: 'WHATSAPP_FLOWS.VALIDATION.NO_FOOTER',
        args: { screen: prefix },
      });
    } else if (footers.length > 1) {
      errors.push({
        screen: sIdx,
        message: 'WHATSAPP_FLOWS.VALIDATION.MULTIPLE_FOOTERS',
        args: { screen: prefix },
      });
    }

    // Footer must be last
    if (
      footers.length === 1 &&
      children[children.length - 1].type !== 'Footer'
    ) {
      errors.push({
        screen: sIdx,
        message: 'WHATSAPP_FLOWS.VALIDATION.FOOTER_NOT_LAST',
        args: { screen: prefix },
      });
    }

    // Validate individual components
    children.forEach((child, cIdx) => {
      const cPrefix = `${prefix}, component ${cIdx + 1}`;

      // Input components need unique variable names
      if (isInputComponent(child.type)) {
        if (!child.name || !child.name.trim()) {
          errors.push({
            screen: sIdx,
            component: cIdx,
            message: 'WHATSAPP_FLOWS.VALIDATION.MISSING_VARIABLE_NAME',
            args: { component: cPrefix },
          });
        } else if (allVariableNames.has(child.name)) {
          errors.push({
            screen: sIdx,
            component: cIdx,
            message: 'WHATSAPP_FLOWS.VALIDATION.DUPLICATE_VARIABLE',
            args: { component: cPrefix, name: child.name },
          });
        }
        allVariableNames.add(child.name);

        if (!child.label || !child.label.trim()) {
          errors.push({
            screen: sIdx,
            component: cIdx,
            message: 'WHATSAPP_FLOWS.VALIDATION.MISSING_LABEL',
            args: { component: cPrefix },
          });
        }
      }

      // Dropdown / RadioButtons / CheckboxGroup need data-source with options
      if (
        ['Dropdown', 'RadioButtonsGroup', 'CheckboxGroup'].includes(child.type)
      ) {
        const options = child['data-source'] || [];
        if (options.length < 1) {
          errors.push({
            screen: sIdx,
            component: cIdx,
            message: 'WHATSAPP_FLOWS.VALIDATION.NO_OPTIONS',
            args: { component: cPrefix },
          });
        }
        // Check for duplicate option IDs
        const optionIds = options.map(o => o.id);
        if (new Set(optionIds).size !== optionIds.length) {
          errors.push({
            screen: sIdx,
            component: cIdx,
            message: 'WHATSAPP_FLOWS.VALIDATION.DUPLICATE_OPTION_IDS',
            args: { component: cPrefix },
          });
        }
      }

      // Footer navigate action must reference a valid screen
      if (child.type === 'Footer') {
        const action = child['on-click-action'];
        if (action?.name === 'navigate') {
          const nextScreen = action.next?.name;
          if (!nextScreen) {
            errors.push({
              screen: sIdx,
              component: cIdx,
              message: 'WHATSAPP_FLOWS.VALIDATION.NAVIGATE_NO_TARGET',
              args: { screen: prefix },
            });
          } else if (nextScreen === screen.id) {
            errors.push({
              screen: sIdx,
              component: cIdx,
              message: 'WHATSAPP_FLOWS.VALIDATION.NAVIGATE_SELF',
              args: { screen: prefix },
            });
          }
        }
      }

      // Display components need text content
      if (
        ['TextHeading', 'TextSubheading', 'TextBody', 'TextCaption'].includes(
          child.type
        )
      ) {
        if (!child.text || !child.text.trim()) {
          errors.push({
            screen: sIdx,
            component: cIdx,
            message: 'WHATSAPP_FLOWS.VALIDATION.EMPTY_TEXT',
            args: { component: cPrefix },
          });
        }
      }
    });
  });

  return errors;
}

// ── Flow template presets ──────────────────────────────────────────
export const FLOW_TEMPLATE_PRESETS = [
  {
    key: 'lead_generation',
    icon: 'i-lucide-user-plus',
    screens: [
      {
        id: 'LEAD_INFO',
        title: 'Contact Information',
        terminal: false,
        layout: {
          type: 'SingleColumnLayout',
          children: [
            { type: 'TextHeading', text: 'Get in Touch' },
            {
              type: 'TextBody',
              text: "We'd love to hear from you. Please fill in your details below.",
            },
            {
              type: 'TextInput',
              label: 'Full Name',
              name: 'full_name',
              required: true,
              'input-type': 'text',
            },
            {
              type: 'TextInput',
              label: 'Email',
              name: 'email',
              required: true,
              'input-type': 'email',
            },
            {
              type: 'TextInput',
              label: 'Phone',
              name: 'phone',
              required: false,
              'input-type': 'phone',
            },
            {
              type: 'Dropdown',
              label: 'How did you hear about us?',
              name: 'source',
              required: false,
              'data-source': [
                { id: '1', title: 'Social Media' },
                { id: '2', title: 'Search Engine' },
                { id: '3', title: 'Friend/Referral' },
                { id: '4', title: 'Other' },
              ],
            },
            {
              type: 'Footer',
              label: 'Submit',
              'on-click-action': {
                name: 'complete',
                payload: {},
              },
            },
          ],
        },
      },
    ],
  },
  {
    key: 'appointment_booking',
    icon: 'i-lucide-calendar-check',
    screens: [
      {
        id: 'BOOKING_DETAILS',
        title: 'Book Appointment',
        terminal: false,
        layout: {
          type: 'SingleColumnLayout',
          children: [
            { type: 'TextHeading', text: 'Schedule Your Appointment' },
            {
              type: 'TextInput',
              label: 'Your Name',
              name: 'name',
              required: true,
              'input-type': 'text',
            },
            {
              type: 'DatePicker',
              label: 'Preferred Date',
              name: 'date',
              required: true,
            },
            {
              type: 'Dropdown',
              label: 'Preferred Time',
              name: 'time_slot',
              required: true,
              'data-source': [
                { id: '1', title: '09:00 AM' },
                { id: '2', title: '10:00 AM' },
                { id: '3', title: '11:00 AM' },
                { id: '4', title: '02:00 PM' },
                { id: '5', title: '03:00 PM' },
                { id: '6', title: '04:00 PM' },
              ],
            },
            {
              type: 'TextArea',
              label: 'Notes (optional)',
              name: 'notes',
              required: false,
            },
            {
              type: 'Footer',
              label: 'Book Now',
              'on-click-action': {
                name: 'complete',
                payload: {},
              },
            },
          ],
        },
      },
    ],
  },
  {
    key: 'customer_survey',
    icon: 'i-lucide-clipboard-list',
    screens: [
      {
        id: 'SURVEY_SCREEN',
        title: 'Feedback Survey',
        terminal: false,
        layout: {
          type: 'SingleColumnLayout',
          children: [
            { type: 'TextHeading', text: 'We Value Your Feedback' },
            {
              type: 'TextBody',
              text: 'Help us improve by answering a few questions.',
            },
            {
              type: 'RadioButtonsGroup',
              label: 'How would you rate our service?',
              name: 'rating',
              required: true,
              'data-source': [
                { id: '1', title: 'Excellent' },
                { id: '2', title: 'Good' },
                { id: '3', title: 'Average' },
                { id: '4', title: 'Poor' },
              ],
            },
            {
              type: 'CheckboxGroup',
              label: 'What did you like?',
              name: 'liked_aspects',
              required: false,
              'data-source': [
                { id: '1', title: 'Response Time' },
                { id: '2', title: 'Product Quality' },
                { id: '3', title: 'Customer Support' },
                { id: '4', title: 'Pricing' },
              ],
            },
            {
              type: 'TextArea',
              label: 'Additional Comments',
              name: 'comments',
              required: false,
            },
            {
              type: 'Footer',
              label: 'Submit Feedback',
              'on-click-action': {
                name: 'complete',
                payload: {},
              },
            },
          ],
        },
      },
    ],
  },
  {
    key: 'support_intake',
    icon: 'i-lucide-headphones',
    screens: [
      {
        id: 'SUPPORT_INFO',
        title: 'Support Request',
        terminal: false,
        layout: {
          type: 'SingleColumnLayout',
          children: [
            { type: 'TextHeading', text: 'How Can We Help?' },
            {
              type: 'Dropdown',
              label: 'Issue Category',
              name: 'category',
              required: true,
              'data-source': [
                { id: '1', title: 'Billing' },
                { id: '2', title: 'Technical Issue' },
                { id: '3', title: 'Account Access' },
                { id: '4', title: 'General Inquiry' },
              ],
            },
            {
              type: 'TextInput',
              label: 'Subject',
              name: 'subject',
              required: true,
              'input-type': 'text',
            },
            {
              type: 'TextArea',
              label: 'Describe your issue',
              name: 'description',
              required: true,
            },
            {
              type: 'Footer',
              label: 'Submit Request',
              'on-click-action': {
                name: 'complete',
                payload: {},
              },
            },
          ],
        },
      },
    ],
  },
];
